import React, { useState, useEffect, useCallback } from 'react';
import { fragmentTag } from '../utils/wordMap';
import AudioEditorModal from './AudioEditorModal';
import { applyTtsConfigAsLast } from './SpeechGeneration';
import './AudioVersions.css';

// Voice-over version manager (editor / TEST only).
//
// Lists every uploaded version for a (location, field), shows which one is
// active, and lets you preview, activate, download or delete each. The active
// version is what plays in the app and what "Sync to Production" promotes.
//
// It configures itself from the `audioUrl` prop, which already carries
// uuid / questId / table in its query string — so no extra wiring is needed.

const formatBytes = (bytes, decimals = 1) => {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(decimals))} ${sizes[i]}`;
};

const pad = (n) => String(n).padStart(2, '0');

const formatDate = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

// "description" -> "Description", "after_message" -> "After message"
const prettyField = (field) => {
  if (!field) return 'Audio';
  const s = field.replace(/_/g, ' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
};

// What a generated take was voiced with (migration 028), folded under the
// version: the settings as they went to Google, the text after the glossary,
// and a button that makes them the last-used ones for the next take.
const TtsSettings = ({ version: v, isReadOnly }) => {
  const [note, setNote] = useState(null);
  const c = v.tts_config;
  if (!c) return null;
  const use = () => {
    try {
      setNote(applyTtsConfigAsLast(c)
        ? 'Saved as the last-used settings: the studio\'s next take uses them; reopen the generation panel to see them there.'
        : 'This record has no panel fields, so the panel can\'t be set from it.');
    } catch (e) {
      setNote(`Could not save the settings: ${e.message}`);
    }
  };
  const styles = c.styles ? Object.entries(c.styles) : [];
  return (
    <details className="audio-version-tts">
      <summary>TTS settings #{v.tts_config_id}</summary>
      <dl>
        <dt>Model</dt><dd>{c.model} ({c.format} shape)</dd>
        <dt>Scheme</dt><dd>{c.scheme}</dd>
        <dt>Temperature</dt><dd>{c.temperature}</dd>
        <dt>Voices</dt><dd>Speaker 1: {c.voices.speaker1} · Speaker 2: {c.voices.speaker2}</dd>
      </dl>
      {c.system_prompt && (<><div className="audio-version-tts-h">Prompt</div><pre>{c.system_prompt}</pre></>)}
      {styles.map(([k, val]) => (
        <React.Fragment key={k}><div className="audio-version-tts-h">Style, {k}</div><pre>{val}</pre></React.Fragment>
      ))}
      {v.tts_text && (<><div className="audio-version-tts-h">Text sent (glossary applied)</div><pre>{v.tts_text}</pre></>)}
      {!isReadOnly && c.panel && (
        <button type="button" className="audio-version-edit" onClick={use}>Use these settings for the next take</button>
      )}
      {note && <div className="audio-version-tts-note">{note}</div>}
    </details>
  );
};

const extFromMime = (mime) => {
  if (mime === 'audio/mpeg') return 'mp3';
  if (mime === 'audio/wav') return 'wav';
  if (mime === 'audio/ogg') return 'ogg';
  return 'dat';
};

// `hideEditor` is for hosts that edit tracks themselves — the audio studio has
// its own editor in the right half, and offering a modal on top of it would be
// two editors fighting over the same version.
const AudioVersions = ({ locationId, fieldType, audioUrl, isReadOnly, onVersionsChanged, hideEditor }) => {
  const [versions, setVersions] = useState([]);
  const [previewId, setPreviewId] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);
  const [collapsed, setCollapsed] = useState(true);
  const [editingVersion, setEditingVersion] = useState(null);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');

  // Pull uuid / questId / table out of the audio URL the parent already built.
  const cfg = (() => {
    if (!audioUrl) return null;
    const qIndex = audioUrl.indexOf('?');
    if (qIndex === -1) return null;
    const params = new URLSearchParams(audioUrl.slice(qIndex + 1));
    const uuid = params.get('uuid');
    const table = params.get('table') || 'quests_test';
    const questId = params.get('questId');
    if (!uuid) return null;
    return { uuid, table, questId };
  })();

  // Versions only exist in the editor/test environment.
  const enabled = !!cfg && cfg.table === 'quests_test';

  const fetchVersions = useCallback(async () => {
    if (!enabled) return;
    try {
      const res = await fetch(`/api/audio-versions/${cfg.uuid}/${fieldType}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to load versions');
      setVersions(await res.json());
      setError(null);
    } catch (err) {
      console.error('AudioVersions load error:', err);
      setError(err.message);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, cfg && cfg.uuid, fieldType]);

  // Reload on mount and whenever the audio URL changes (e.g. after an upload,
  // whose cache-busting timestamp changes the URL).
  useEffect(() => { fetchVersions(); }, [fetchVersions, audioUrl]);

  if (!enabled || versions.length < 1) return null;

  const previewSrc = (id) =>
    `/api/audio/${locationId}/${fieldType}?uuid=${cfg.uuid}&questId=${cfg.questId}&table=${cfg.table}&versionId=${id}`;

  const downloadUrl = (v, original) => {
    const ext = original ? extFromMime(v.original_mime_type) : 'ogg';
    return `/api/download-audio/${cfg.questId}/${fieldType}/${cfg.uuid}.${ext}?table=${cfg.table}&version=${v.id}`;
  };

  const handleActivate = async (id) => {
    setBusyId(id);
    try {
      const res = await fetch(`/api/audio-versions/${id}/activate`, { method: 'POST', credentials: 'include' });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.error || 'Failed to activate');
      }
      await fetchVersions();
      if (onVersionsChanged) onVersionsChanged();
    } catch (err) {
      console.error('Activate error:', err);
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const startRename = (v) => { setRenamingId(v.id); setRenameValue(v.label || v.filename || ''); };
  const cancelRename = () => { setRenamingId(null); setRenameValue(''); };
  const saveRename = async (id) => {
    try {
      const res = await fetch(`/api/audio-versions/${id}/label`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ label: renameValue }),
      });
      if (!res.ok) throw new Error('Failed to rename');
      cancelRename();
      await fetchVersions();
    } catch (err) {
      console.error('Rename error:', err);
      setError(err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this version? This cannot be undone.')) return;
    setBusyId(id);
    try {
      const res = await fetch(`/api/audio-versions/${id}`, { method: 'DELETE', credentials: 'include' });
      if (!res.ok) throw new Error('Failed to delete');
      if (previewId === id) setPreviewId(null);
      await fetchVersions();
      if (onVersionsChanged) onVersionsChanged();
    } catch (err) {
      console.error('Delete error:', err);
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="audio-versions">
      <button
        type="button"
        className="audio-versions-title"
        aria-expanded={!collapsed}
        onClick={() => setCollapsed((c) => !c)}
      >
        <span className="audio-versions-caret">{collapsed ? '▸' : '▾'}</span>
        {prettyField(fieldType)} audio versions ({versions.length})
      </button>
      {!collapsed && error && <div className="audio-versions-error">{error}</div>}
      {!collapsed && (
      <ul className="audio-versions-list">
        {versions.map((v) => (
          <li key={v.id} className={`audio-version-item${v.is_active ? ' active' : ''}`}>
            <div className="audio-version-row">
              <button
                type="button"
                className="audio-version-play"
                title="Preview this version"
                onClick={() => setPreviewId(previewId === v.id ? null : v.id)}
              >
                {previewId === v.id ? '⏸' : '▶'}
              </button>
              {renamingId === v.id ? (
                <span className="audio-version-rename-edit">
                  <input
                    autoFocus
                    className="audio-version-rename-input"
                    value={renameValue}
                    placeholder={v.filename}
                    onChange={(e) => setRenameValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') saveRename(v.id);
                      if (e.key === 'Escape') cancelRename();
                    }}
                  />
                  <button type="button" title="Save name" onMouseDown={(e) => e.preventDefault()} onClick={() => saveRename(v.id)}>✓</button>
                  <button type="button" title="Cancel" onMouseDown={(e) => e.preventDefault()} onClick={cancelRename}>✕</button>
                </span>
              ) : (
                <>
                  <span className="audio-version-name" title={v.filename}>{v.label || v.filename}{fragmentTag(v)}</span>
                  {!isReadOnly && (
                    <button
                      type="button"
                      className="audio-version-rename"
                      title="Rename this version"
                      onClick={() => startRename(v)}
                    >
                      ✏️
                    </button>
                  )}
                </>
              )}
              {v.is_active && <span className="audio-version-badge">active</span>}
            </div>
            <div className="audio-version-meta">
              <span>{formatDate(v.uploaded_at)}</span>
              <span>OGG {formatBytes(v.size)}</span>
              <a href={downloadUrl(v, false)} download className="audio-version-link">OGG ⬇</a>
              {v.original_id && (
                <a href={downloadUrl(v, true)} download className="audio-version-link">
                  {extFromMime(v.original_mime_type).toUpperCase()} ⬇
                </a>
              )}
            </div>
            <TtsSettings version={v} isReadOnly={isReadOnly} />
            {previewId === v.id && (
              <audio controls preload="metadata" style={{ width: '100%', marginTop: 4 }} src={previewSrc(v.id)} />
            )}
            <div className="audio-version-actions">
              {!v.is_active && !v.is_fragment && (
                <button
                  type="button"
                  className="audio-version-activate"
                  disabled={isReadOnly || busyId === v.id}
                  onClick={() => handleActivate(v.id)}
                >
                  Make active
                </button>
              )}
              {!hideEditor && (
              <button
                type="button"
                className="audio-version-edit"
                title="Open waveform editor"
                disabled={isReadOnly}
                onClick={() => setEditingVersion(v)}
              >
                ✂️ Edit
              </button>
              )}
              <button
                type="button"
                className="audio-version-delete"
                title="Delete this version"
                disabled={isReadOnly || busyId === v.id}
                onClick={() => handleDelete(v.id)}
              >
                🗑️
              </button>
            </div>
          </li>
        ))}
      </ul>
      )}
      {editingVersion && !hideEditor && (
        <AudioEditorModal
          questId={cfg.questId}
          locationId={locationId}
          fieldType={fieldType}
          uuid={cfg.uuid}
          table={cfg.table}
          versions={versions}
          editVersion={editingVersion}
          onClose={() => setEditingVersion(null)}
          onSaved={async () => { await fetchVersions(); if (onVersionsChanged) onVersionsChanged(); }}
        />
      )}
    </div>
  );
};

export default AudioVersions;
